import { useQuery } from '@tanstack/react-query';
import { clusterApi } from '@/api/client';

const STALE_TIME = 15_000;
const POLL_INTERVAL = 15_000;
const FAST_POLL_INTERVAL = 10_000;

export function useClusterNodes(siteId: string) {
  return useQuery({
    queryKey: ['cluster-nodes', siteId],
    queryFn: () => clusterApi.getNodes(siteId),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId,
  });
}

export function useClusterPods(
  siteId: string,
  filters?: { namespace?: string; node?: string; search?: string; phase?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['cluster-pods', siteId, filters],
    queryFn: () => clusterApi.getPods(siteId, filters),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && enabled,
  });
}

export function useClusterHealth(siteId: string) {
  return useQuery({
    queryKey: ['cluster-health-live', siteId],
    queryFn: () => clusterApi.getHealth(siteId),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId,
  });
}

export function useClusterEvents(
  siteId: string,
  filters?: { namespace?: string; type?: string; limit?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['cluster-events', siteId, filters],
    queryFn: () => clusterApi.getEvents(siteId, filters),
    refetchInterval: FAST_POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && enabled,
  });
}

export function useClusterNamespaces(siteId: string) {
  return useQuery({
    queryKey: ['cluster-namespaces', siteId],
    queryFn: () => clusterApi.getNamespaces(siteId),
    staleTime: 60_000,
    enabled: !!siteId,
  });
}

export function usePodDetail(siteId: string, namespace: string, podName: string) {
  return useQuery({
    queryKey: ['pod-detail', siteId, namespace, podName],
    queryFn: () => clusterApi.getPodDetail(siteId, namespace, podName),
    refetchInterval: FAST_POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && !!namespace && !!podName,
  });
}

export function usePodLogs(
  siteId: string,
  namespace: string,
  podName: string,
  params?: { container?: string; tailLines?: number; sinceSeconds?: number; previous?: boolean },
) {
  return useQuery({
    queryKey: ['pod-logs', siteId, namespace, podName, params],
    queryFn: () => clusterApi.getPodLogs(siteId, namespace, podName, params),
    refetchInterval: FAST_POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && !!namespace && !!podName,
  });
}

export function useClusterServices(siteId: string, namespace?: string, enabled = true) {
  return useQuery({
    queryKey: ['cluster-services', siteId, namespace],
    queryFn: () => clusterApi.getServices(siteId, namespace),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && enabled,
  });
}

export function useServiceEndpoints(siteId: string, namespace: string, serviceName: string) {
  return useQuery({
    queryKey: ['service-endpoints', siteId, namespace, serviceName],
    queryFn: () => clusterApi.getServiceEndpoints(siteId, namespace, serviceName),
    staleTime: STALE_TIME,
    enabled: !!siteId && !!namespace && !!serviceName,
  });
}

export function useClusterStorage(siteId: string, params?: { namespace?: string; status?: string }, enabled = true) {
  return useQuery({
    queryKey: ['cluster-storage', siteId, params],
    queryFn: () => clusterApi.getStorage(siteId, params),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && enabled,
  });
}

export function usePodMetrics(siteId: string, enabled = true) {
  return useQuery({
    queryKey: ['pod-metrics', siteId],
    queryFn: () => clusterApi.getPodMetrics(siteId),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && enabled,
  });
}

export function useClusterDeployments(siteId: string, namespace?: string, enabled = true) {
  return useQuery({
    queryKey: ['cluster-deployments', siteId, namespace],
    queryFn: () => clusterApi.getDeployments(siteId, namespace),
    refetchInterval: POLL_INTERVAL,
    staleTime: STALE_TIME,
    enabled: !!siteId && enabled,
  });
}

// Workload card grid (#234) — operator-facing aggregator for Deployments,
// StatefulSets, and DaemonSets. 5s poll intentionally tighter than the
// other cluster panels: this is the "is anything broken?" surface.
const WORKLOADS_POLL_INTERVAL = 5_000;

export function useClusterWorkloads(siteId: string, enabled = true) {
  return useQuery({
    queryKey: ['cluster-workloads', siteId],
    queryFn: () => clusterApi.getWorkloads(siteId),
    refetchInterval: WORKLOADS_POLL_INTERVAL,
    staleTime: WORKLOADS_POLL_INTERVAL,
    enabled: !!siteId && enabled,
  });
}

// Workload drill-down drawer (#241) — same 5s cadence as the parent panel
// for the Current view; History is intentionally not refetched on an
// interval (it changes only when a pod terminates, which the next Current
// poll will surface anyway).
export function useWorkloadPods(
  siteId: string,
  namespace: string,
  kind: 'deployment' | 'statefulset' | 'daemonset',
  name: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['workload-pods', siteId, namespace, kind, name],
    queryFn: () => clusterApi.getWorkloadPods(siteId, namespace, kind, name),
    refetchInterval: WORKLOADS_POLL_INTERVAL,
    staleTime: WORKLOADS_POLL_INTERVAL,
    enabled: !!siteId && !!namespace && !!kind && !!name && enabled,
  });
}

// Secrets list (#251) — engineer-tier panel. No polling: the data is the
// metadata of secrets in a namespace, which changes far slower than pods,
// and we don't want to inflate audit volume on the per-secret reveal route
// (the *list* doesn't audit but the operator still expects the page to
// feel "static" until they click). Stale 60s so a manual refresh works.
export function useSecretsList(
  siteId: string,
  params?: { namespace?: string; type?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['cluster-secrets', siteId, params],
    queryFn: () => clusterApi.listSecrets(siteId, params),
    staleTime: 60_000,
    enabled: !!siteId && enabled,
  });
}

// Workload-scoped events feed (#243) — same 5s cadence as the pods view.
// Mirrors useWorkloadPods exactly: short staleTime so a tab switch refetches
// promptly; refetchInterval keeps the active tab fresh.
export function useWorkloadEvents(
  siteId: string,
  namespace: string,
  kind: 'deployment' | 'statefulset' | 'daemonset',
  name: string,
  window: '1h' | '4h' | '24h' = '1h',
  enabled = true,
) {
  return useQuery({
    queryKey: ['workload-events', siteId, namespace, kind, name, window],
    queryFn: () => clusterApi.getWorkloadEvents(siteId, namespace, kind, name, window),
    refetchInterval: WORKLOADS_POLL_INTERVAL,
    staleTime: WORKLOADS_POLL_INTERVAL,
    enabled: !!siteId && !!namespace && !!kind && !!name && enabled,
  });
}

// Recommendations engine (#260) — slower 30s cadence. The matched-pattern
// list does not churn between event polls, and the underlying server-side
// computation walks all pods + events for the workload, so we do not want
// to hammer it at the 5s pace of the live pod/event tabs.
const RECOMMENDATIONS_POLL_INTERVAL = 30_000;
export function useWorkloadRecommendations(
  siteId: string,
  namespace: string,
  kind: 'deployment' | 'statefulset' | 'daemonset',
  name: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['workload-recommendations', siteId, namespace, kind, name],
    queryFn: () => clusterApi.getWorkloadRecommendations(siteId, namespace, kind, name),
    refetchInterval: RECOMMENDATIONS_POLL_INTERVAL,
    staleTime: RECOMMENDATIONS_POLL_INTERVAL,
    enabled: !!siteId && !!namespace && !!kind && !!name && enabled,
  });
}
